import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, Search, X, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export function SerialNumberCombobox({
  serialNumbers = [],
  value,
  onChange,
  placeholder = "Gerät auswählen...",
  emptyText = "Keine Geräte verfügbar",
  disabled = false,
  allowClear = true,
  testid = "serial-combobox"
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const snList = useMemo(() => (Array.isArray(serialNumbers) ? serialNumbers : []), [serialNumbers]);

  const selected = useMemo(
    () => snList.find((s) => s.id === value) || null,
    [snList, value]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = !term
      ? snList
      : snList.filter((s) => {
          const haystack = [s.serial_number, s.product_name, s.category]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(term);
        });
    return [...list].sort((a, b) =>
      (a.product_name || "").localeCompare(b.product_name || "", "de")
    );
  }, [snList, search]);

  const handleSelect = (id) => {
    onChange(id === value ? "" : id);
    setOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  if (disabled || snList.length === 0) {
    return (
      <Button
        type="button"
        variant="outline"
        disabled
        className="w-full justify-between h-11 md:h-10 font-normal text-muted-foreground"
        data-testid={`${testid}-disabled`}
      >
        <span>{emptyText}</span>
        <ChevronsUpDown className="w-4 h-4 opacity-30" />
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-11 md:h-10 font-normal",
            !selected && "text-muted-foreground"
          )}
          data-testid={testid}
        >
          {selected ? (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Hash className="w-4 h-4 shrink-0 text-emerald-600" />
              <span className="font-mono text-sm truncate">{selected.serial_number}</span>
              <span className="hidden sm:inline text-xs text-muted-foreground truncate">
                {selected.product_name}
              </span>
            </div>
          ) : (
            <span className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              {placeholder}
            </span>
          )}
          <div className="flex items-center gap-1 shrink-0">
            {selected && allowClear && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") handleClear(e);
                }}
                className="p-1 hover:bg-muted rounded cursor-pointer"
                data-testid={`${testid}-clear`}
                aria-label="Auswahl löschen"
              >
                <X className="w-3.5 h-3.5 opacity-60 hover:opacity-100" />
              </span>
            )}
            <ChevronsUpDown className="w-4 h-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        sideOffset={4}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Suche Seriennr. oder Gerätename..."
            className="h-11"
            value={search}
            onValueChange={setSearch}
            data-testid={`${testid}-search`}
          />
          <CommandList className="max-h-[320px]">
            {filtered.length === 0 ? (
              <CommandEmpty>
                <div className="py-6 text-center text-sm">
                  <Search className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-muted-foreground">Keine Geräte gefunden</p>
                </div>
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {filtered.map((sn) => {
                  const isSelected = sn.id === value;
                  return (
                    <CommandItem
                      key={sn.id}
                      value={sn.id}
                      onSelect={handleSelect}
                      className="flex items-start gap-3 py-2.5"
                      data-testid={`${testid}-item-${sn.id}`}
                    >
                      <Check
                        className={cn(
                          "w-4 h-4 mt-1 shrink-0",
                          isSelected ? "opacity-100 text-primary" : "opacity-0"
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Hash className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <span className="font-mono text-sm font-medium truncate">{sn.serial_number}</span>
                        </div>
                        {sn.product_name && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{sn.product_name}</p>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
